/** Returns true if the given option is currently hidden by its showWhen rule. */
function isHidden(opt, appearance) {
    if (!opt.showWhen)
        return false;
    const { option, not, in: inList, notIn } = opt.showWhen;
    const refSel = appearance.selections[option];
    if (!refSel || refSel.type !== 'choice')
        return false;
    const choiceId = refSel.choiceId;
    if (not !== undefined && choiceId === not)
        return true;
    if (inList !== undefined && !inList.includes(choiceId))
        return true;
    if (notIn !== undefined && notIn.includes(choiceId))
        return true;
    return false;
}
/**
 * Converts stored character appearance selections into a CharacterDefinition
 * suitable for sprite assembly. The mapping is driven entirely by the
 * character-customization-config.json config — no hardcoded field names or layer numbers.
 */
export function appearanceToCharacterDefinition(appearance, config) {
    const layers = {};
    const resolveTint = (optId) => {
        const opt = config.options.find(o => o.id === optId);
        if (!opt)
            return undefined;
        if (isHidden(opt, appearance))
            return undefined;
        const sel = appearance.selections[optId];
        return sel?.type === 'color' ? sel.hex : undefined;
    };
    // --- Kit options ---
    for (const opt of config.options) {
        if (opt.mode !== 'kit')
            continue;
        const sel = appearance.selections[opt.id];
        if (!sel || sel.type !== 'choice')
            continue;
        const choice = opt.choices?.find(c => c.id === sel.choiceId);
        if (!choice?.additionalLayers)
            continue;
        for (const extra of choice.additionalLayers) {
            const tint = extra.tintOptionId ? resolveTint(extra.tintOptionId) : undefined;
            layers[String(extra.layer)] = extra.part ? { part: extra.part, tint } : null;
        }
    }
    // --- Layer-based options ---
    const byLayer = new Map();
    for (const opt of config.options) {
        if (opt.mode === 'kit' || opt.layer === undefined)
            continue;
        const existing = byLayer.get(opt.layer) ?? [];
        existing.push(opt);
        byLayer.set(opt.layer, existing);
    }
    for (const [layerNum, opts] of byLayer) {
        const layerKey = String(layerNum);
        if (layerKey in layers)
            continue;
        const toggleOpt = opts.find(o => o.inputType === 'toggleWithColor');
        const partOpt = opts.find(o => o.mode === 'part');
        const tintOpt = opts.find(o => o.mode === 'tint');
        const variantOpt = opts.find(o => o.mode === 'variant');
        if (toggleOpt) {
            const sel = appearance.selections[toggleOpt.id];
            if (!sel || sel.type !== 'toggle' || !sel.enabled) {
                layers[layerKey] = null;
                continue;
            }
            const part = toggleOpt.part ?? '';
            layers[layerKey] = { part, tint: sel.hex };
            continue;
        }
        if (partOpt) {
            const partSel = appearance.selections[partOpt.id];
            if (!partSel || partSel.type !== 'choice') {
                layers[layerKey] = null;
                continue;
            }
            const choice = partOpt.choices?.find(c => c.id === partSel.choiceId);
            if (!choice || !choice.part) {
                layers[layerKey] = null;
                continue;
            }
            let tint;
            if (tintOpt) {
                const hidden = isHidden(tintOpt, appearance);
                if (!hidden) {
                    const tintSel = appearance.selections[tintOpt.id];
                    if (tintSel?.type === 'color')
                        tint = tintSel.hex;
                }
            }
            layers[layerKey] = { part: choice.part, tint };
            if (choice.additionalLayers) {
                for (const extra of choice.additionalLayers) {
                    const extraTint = extra.tintOptionId ? resolveTint(extra.tintOptionId) : tint;
                    layers[String(extra.layer)] = extra.part ? { part: extra.part, tint: extraTint } : null;
                }
            }
            continue;
        }
        if (variantOpt) {
            const variantSel = appearance.selections[variantOpt.id];
            if (!variantSel || variantSel.type !== 'choice') {
                layers[layerKey] = null;
                continue;
            }
            const part = variantOpt.part ?? '';
            let tint;
            if (tintOpt) {
                const tintSel = appearance.selections[tintOpt.id];
                if (tintSel?.type === 'color')
                    tint = tintSel.hex;
            }
            layers[layerKey] = { part, variant: variantSel.choiceId, tint };
            continue;
        }
        if (tintOpt) {
            if (isHidden(tintOpt, appearance)) {
                layers[layerKey] = null;
                continue;
            }
            const tintSel = appearance.selections[tintOpt.id];
            if (!tintSel || tintSel.type !== 'color') {
                layers[layerKey] = null;
                continue;
            }
            const part = tintOpt.part ?? '';
            layers[layerKey] = { part, tint: tintSel.hex };
            continue;
        }
    }
    return { layers };
}
/**
 * Applies equipment overrides on top of a base CharacterDefinition.
 */
export function applyEquipmentOverrides(base, overrides) {
    const layers = { ...base.layers };
    if (overrides.layers) {
        for (const [key, val] of Object.entries(overrides.layers)) {
            layers[key] = val;
        }
    }
    return {
        layers,
        animationOverrides: { ...base.animationOverrides, ...overrides.animationOverrides },
    };
}
